"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

const floatingCards = [
{
  id: 1,
  image: "https://images.unsplash.com/photo-1622656507511-27f0a12f86b6",
  alt: "Red road bicycle on white background, side profile view",
  label: "Road Pro X9",
  price: "$2,499",
  badge: "Bestseller",
  className: "float-a",
  style: { top: "8%", left: "2%", width: "200px" },
  rotation: "-4deg"
},
{
  id: 2,
  image: "https://images.unsplash.com/photo-1621506322261-d5075d855845",
  alt: "Black mountain bike with full suspension on rocky trail",
  label: "Trail Blazer",
  price: "$3,199",
  badge: "New",
  className: "float-b",
  style: { top: "4%", right: "4%", width: "190px" },
  rotation: "3deg"
},
{
  id: 3,
  image: "https://images.unsplash.com/photo-1454741548190-857344f2efb5",
  alt: "Silver city commuter bicycle parked on urban street",
  label: "Urban Glide",
  price: "$899",
  badge: "Popular",
  className: "float-c",
  style: { bottom: "12%", left: "1%", width: "185px" },
  rotation: "-2deg"
},
{
  id: 4,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ad81f789-1766158199961.png",
  alt: "Yellow electric bicycle with battery pack on city background",
  label: "E-Volt 500",
  price: "$4,199",
  badge: "Electric",
  className: "float-d",
  style: { bottom: "10%", right: "2%", width: "195px" },
  rotation: "5deg"
}];


export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current) return;
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      const xRatio = (clientX / innerWidth - 0.5) * 12;
      const yRatio = (clientY / innerHeight - 0.5) * 8;
      const cards = heroRef.current.querySelectorAll<HTMLElement>(".parallax-card");
      cards.forEach((card, i) => {
        const depth = (i + 1) * 0.4;
        card.style.transform = `translate(${xRatio * depth}px, ${yRatio * depth}px)`;
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden pt-28 pb-16"
      style={{ background: "linear-gradient(160deg, #FAF7F2 0%, #F3EDE0 60%, #EDE5D0 100%)" }}>
      
      {/* Background decorative circles */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full border border-stone-border/40 pointer-events-none" />
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full border border-stone-border/60 pointer-events-none" />
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[300px] h-[300px] rounded-full border border-amber/20 pointer-events-none" />

      {/* Floating product cards */}
      {floatingCards.map((card) =>
      <div
        key={card.id}
        className={`parallax-card absolute z-20 ${card.className} hidden lg:block`}
        style={{
          ...card.style,
          transition: "transform 0.1s ease-out"
        }}>
        
          <div
          className="glass-card rounded-2xl overflow-hidden shadow-card"
          style={{ width: card.style.width }}>
          
            <div className="relative overflow-hidden" style={{ height: "120px" }}>
              <AppImage
              src={card.image}
              alt={card.alt}
              fill
              className="object-cover" />
            
              <span className="amber-badge absolute top-2 left-2 px-2 py-0.5 rounded-full">
                {card.badge}
              </span>
            </div>
            <div className="p-3">
              <p className="font-body font-600 text-xs text-charcoal font-semibold">{card.label}</p>
              <p className="font-heading text-sm font-bold text-amber-dark">{card.price}</p>
            </div>
          </div>
        </div>
      )}

      {/* Hero Content */}
      <div className="relative z-10 text-center max-w-4xl mx-auto px-6">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 amber-badge px-4 py-1.5 rounded-full mb-8 shadow-sm">
          <span className="w-1.5 h-1.5 rounded-full bg-charcoal animate-pulse" />
          <span>New 2026 Collection Arrived</span>
        </div>

        {/* Headline */}
        <h1 className="font-heading text-[clamp(3rem,9vw,7.5rem)] font-black leading-[0.9] tracking-tight text-charcoal mb-6">
          Ride Beyond
          <br />
          <span className="italic font-light text-amber" style={{ fontStyle: "italic" }}>
            the Horizon.
          </span>
        </h1>

        {/* Subheadline */}
        <p className="text-lg md:text-xl text-stone-warm max-w-xl mx-auto leading-relaxed mb-10">
          Premium bicycles handpicked for every terrain. From city streets to mountain peaks — find your perfect ride at VelocityCycles.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="#bikes"
            className="btn-amber flex items-center justify-center gap-2 px-8 py-4 rounded-full text-base">
            
            Explore Our Bikes
            <Icon name="ArrowRightIcon" size={18} className="text-charcoal" />
          </a>
          <a
            href="#story"
            className="btn-outline flex items-center justify-center gap-2 px-8 py-4 rounded-full text-base">
            
            Our Story
          </a>
        </div>

        {/* Trust indicators */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-6 text-sm text-stone-warm">
          <div className="flex items-center gap-1.5">
            <Icon name="StarIcon" size={16} className="text-amber" variant="solid" />
            <span className="font-semibold text-charcoal">4.9/5</span> from 1,200+ reviews
          </div>
          <div className="w-px h-4 bg-stone-border hidden sm:block" />
          <div className="flex items-center gap-1.5">
            <Icon name="TruckIcon" size={16} className="text-amber" />
            Free shipping over $500
          </div>
          <div className="w-px h-4 bg-stone-border hidden sm:block" />
          <div className="flex items-center gap-1.5">
            <Icon name="ShieldCheckIcon" size={16} className="text-amber" />
            3-year warranty
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-xs font-medium text-stone-warm uppercase tracking-widest">Scroll</span>
        <Icon name="ChevronDownIcon" size={18} className="text-stone-warm" />
      </div>
    </section>);

}